import React, { useState } from 'react';
import { propertiesAPI } from '../lib/api';
import { toast } from 'sonner';
import { useTranslation } from '../lib/i18n';
import { track } from '../lib/analytics';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import {
  Loader2, CheckCircle2, Search, ExternalLink, Link as LinkIcon,
} from 'lucide-react';

/**
 * Self-contained Willhaben-import flow. Drop this anywhere a user might
 * want to add a property from a willhaben.at listing. Currently used
 * in FirstRenderPage (onboarding) and NewPropertyModal (paying users
 * adding additional listings).
 *
 * Owns all of its internal state (URL, scraped data, image selection,
 * agreement). Parent only gets notified via `onImported(property)`
 * after a successful POST /api/properties/import-from-willhaben.
 *
 * Two visual states inside one card:
 *   1. URL-paste:   no scraped data yet → input + "Inserat laden" CTA
 *   2. Confirm:     scraped data present → review + image grid + agree
 *                   checkbox + "Importieren" CTA
 *
 * The image-mirroring happens server-side during the import call –
 * by the time `onImported` fires, the property's `images` already
 * point at our own storage, not willhaben CDN.
 *
 * Props
 *   onImported(property): called with the saved Property dict on
 *                         successful import. Parent decides what
 *                         happens next (close modal / reload list /
 *                         set as render target / etc).
 *   variant:              'card' (default) or 'embedded'. Embedded
 *                         drops the outer Card wrapper for use inside
 *                         a modal that already has its own padding.
 */
export default function WillhabenImportFlow({ onImported, onImportMoreFromBroker, variant = 'card' }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [scraped, setScraped] = useState(null);
  const [selectedSet, setSelectedSet] = useState(null);
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  // After a successful single-listing import, if the scraped data
  // exposed an org_id, we offer the user a quick "import more from
  // this broker?" CTA. Holds the data we need to route into the
  // broker flow without re-entering the URL.
  const [justImportedFromBroker, setJustImportedFromBroker] = useState(null); // {orgId, propertyId, title}

  const selectedCount = selectedSet ? selectedSet.size : 0;
  const canImport = !!scraped && agree && selectedCount >= 3 && !importing;

  const handlePreview = async (e) => {
    e?.preventDefault();
    const trimmed = (url || '').trim();
    if (!trimmed) {
      toast.error(t('firstRender.willhabenNeedUrl')
        || 'Bitte eine Willhaben-URL einfügen.');
      return;
    }
    setLoading(true);
    try {
      const res = await propertiesAPI.willhabenPreview(trimmed);
      const data = res.data;
      setScraped(data);
      const cap = Math.min(30, (data.images || []).length);
      setSelectedSet(new Set(Array.from({ length: cap }, (_, i) => i)));
      setAgree(false);
      track('willhaben_previewed', {
        listing_id: data.listing_id,
        image_count: (data.images || []).length,
      });
    } catch (err) {
      toast.error(
        err.response?.data?.detail
        || t('firstRender.willhabenFailed')
        || 'Willhaben-Inserat konnte nicht geladen werden.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggleImage = (idx) => {
    setSelectedSet((prev) => {
      const next = new Set(prev || []);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const handleReset = () => {
    setScraped(null);
    setSelectedSet(null);
    setAgree(false);
    // Keep URL so user can edit it instead of re-pasting
  };

  const handleImport = async () => {
    if (!scraped) return;
    if (!agree) {
      toast.error(t('firstRender.willhabenAgreeFirst')
        || 'Bitte bestätige, dass das deine Anzeige ist.');
      return;
    }
    const selected = Array.from(selectedSet || []).sort((a, b) => a - b);
    if (selected.length < 3) {
      toast.error(t('firstRender.willhabenPickThree')
        || 'Bitte mindestens 3 Bilder für das Video auswählen.');
      return;
    }
    setImporting(true);
    try {
      const res = await propertiesAPI.willhabenImport({
        url: scraped.source_url || url.trim(),
        agreement: true,
        selectedImages: selected,
      });
      toast.success(t('firstRender.willhabenImported')
        || 'Anzeige importiert. Wähle jetzt eine Vorlage.');
      track('willhaben_imported', {
        listing_id: scraped.listing_id,
        selected_count: selected.length,
      });
      // If the broker info is available + parent supports the prompt,
      // pause for the "import more from this broker?" question instead
      // of closing immediately. Otherwise behave as before.
      const orgId = scraped.org_id;
      if (orgId && onImportMoreFromBroker) {
        setJustImportedFromBroker({
          orgId,
          property: res.data,
          title: scraped.title,
        });
      } else {
        onImported?.(res.data);
      }
    } catch (err) {
      toast.error(
        err.response?.data?.detail
        || t('firstRender.willhabenImportFailed')
        || 'Import fehlgeschlagen. Bitte erneut versuchen.'
      );
    } finally {
      setImporting(false);
    }
  };

  const Wrapper = ({ children }) =>
    variant === 'embedded' ? (
      <div className="space-y-4">{children}</div>
    ) : (
      <Card className="glass border-[#1f4d3a]/30 mb-8 overflow-hidden">
        <CardContent className="p-5 sm:p-6 space-y-5">
          {children}
        </CardContent>
      </Card>
    );

  // ── State 3: post-import "import more from this broker?" ──────────
  // Only shown when the parent passed onImportMoreFromBroker AND we
  // know the broker's orgId. Otherwise the import flow closes via
  // onImported() right after success.
  if (justImportedFromBroker) {
    return (
      <Wrapper>
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-[#e4f0e8] border border-[#1e7a4f]/40 shrink-0">
            <CheckCircle2 className="w-5 h-5 text-[#1e7a4f]" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-[#23211a] mb-0.5">
              {t('firstRender.willhabenImportedTitle') || 'Inserat importiert!'}
            </h3>
            <p className="text-xs text-[#6f6a5c] leading-relaxed truncate">
              {justImportedFromBroker.title}
            </p>
          </div>
        </div>

        <div className="rounded-lg bg-[#f0f3ec] border border-[#1f4d3a]/30 p-4 space-y-3">
          <p className="text-sm text-[#23211a] leading-relaxed">
            {t('firstRender.willhabenAskMore')
              || 'Möchtest du auch weitere Inserate vom selben Anbieter importieren?'}
          </p>
          <div className="flex gap-2 flex-col sm:flex-row">
            <Button
              type="button"
              onClick={() => {
                onImportMoreFromBroker?.(justImportedFromBroker.orgId, justImportedFromBroker.property);
              }}
              className="flex-1 bg-[#1f4d3a] hover:bg-[#163a2c] text-white"
              data-testid="willhaben-import-more"
            >
              <LinkIcon className="w-4 h-4 mr-2" />
              {t('firstRender.willhabenImportMore') || 'Mehr vom Anbieter importieren'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                onImported?.(justImportedFromBroker.property);
              }}
              className="text-[#4a463b] hover:bg-[#efe9db]"
              data-testid="willhaben-import-done"
            >
              {t('firstRender.willhabenDone') || 'Fertig'}
            </Button>
          </div>
        </div>
      </Wrapper>
    );
  }

  // ── State 1: URL-paste ────────────────────────────────────────────
  if (!scraped) {
    return (
      <Wrapper>
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-[#e9efe7] border border-[#1f4d3a]/30 shrink-0">
            <LinkIcon className="w-5 h-5 text-[#1f4d3a]" />
          </div>
          <div>
            <h3 className="font-semibold text-[#23211a] mb-1">
              {t('firstRender.willhabenTitle') || 'Inserat von Willhaben importieren'}
            </h3>
            <p className="text-xs text-[#6f6a5c] leading-relaxed">
              {t('firstRender.willhabenSubtitle')
                || 'Füge den Link zu deinem Willhaben-Inserat ein. Wir holen Titel, Preis, Adresse und alle Fotos automatisch. Kein Tippen, kein Hochladen.'}
            </p>
          </div>
        </div>

        <form onSubmit={handlePreview} className="space-y-3" data-testid="willhaben-form">
          <label className="text-sm font-medium text-[#23211a] block">
            {t('firstRender.willhabenUrlLabel') || 'Willhaben-URL'}
          </label>
          <div className="flex gap-2 flex-col sm:flex-row">
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.willhaben.at/iad/immobilien/d/…"
              className="flex-1 bg-[#f7f3ea]/85 border border-[#e3dccb] rounded-md px-3 py-2 text-sm text-[#23211a] focus:outline-none focus:border-[#1f4d3a]"
              required
              disabled={loading}
              data-testid="willhaben-url-input"
            />
            <Button
              type="submit"
              disabled={loading || !url.trim()}
              className="bg-[#1f4d3a] hover:bg-[#163a2c] text-white disabled:bg-[#e9e2d2] disabled:text-[#8a8474] whitespace-nowrap"
              data-testid="willhaben-load-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              {t('firstRender.willhabenLoadBtn') || 'Inserat laden'}
            </Button>
          </div>
          <p className="text-[11px] text-[#8a8474]">
            {t('firstRender.willhabenHint')
              || 'Es muss ein Willhaben-Immobilieninserat sein. Z. B. eine /iad/immobilien/d/… Seite. Wir prüfen vor dem Import noch einmal mit dir.'}
          </p>
        </form>
      </Wrapper>
    );
  }

  // ── State 2: Confirm + select ─────────────────────────────────────
  const images = scraped.images || [];
  const imgCap = Math.min(30, images.length);
  return (
    <Wrapper>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className="p-2 rounded-lg bg-[#c7a869]/15 border border-[#c7a869] shrink-0">
            <LinkIcon className="w-5 h-5 text-[#9a6d1f]" />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-[#23211a] mb-1 truncate">
              {scraped.title}
            </h3>
            <p className="text-base text-[#1f4d3a] font-semibold">{scraped.price}</p>
            <p className="text-sm text-[#6f6a5c]">{scraped.location}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleReset}
          className="text-xs text-[#6f6a5c] hover:text-[#23211a] underline shrink-0"
          data-testid="willhaben-reset"
        >
          {t('firstRender.willhabenChangeUrl') || 'Anderes Inserat'}
        </button>
      </div>

      {(scraped.highlights || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {scraped.highlights.map((h, i) => (
            <span
              key={i}
              className="text-[11px] px-2 py-0.5 rounded-full bg-[#e9e2d2] text-[#4a463b] border border-[#d8d0bd]"
            >
              {h}
            </span>
          ))}
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <label className="text-sm font-medium text-[#23211a]">
            {t('firstRender.willhabenSelectImages') || 'Bilder fürs Video auswählen'}
          </label>
          <span className={`text-[11px] ${selectedCount >= 3 ? 'text-[#1e7a4f]' : 'text-[#9a6d1f]'}`}>
            {t('firstRender.willhabenSelectedCount', {
              selected: selectedCount,
              total: imgCap,
            }) || `${selectedCount} von ${imgCap} ausgewählt${selectedCount < 3 ? ' · mind. 3' : ''}`}
          </span>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 max-h-[460px] overflow-y-auto pr-1">
          {images.slice(0, imgCap).map((u, i) => {
            const selected = selectedSet?.has(i);
            return (
              <button
                key={i}
                type="button"
                onClick={() => handleToggleImage(i)}
                className={`relative aspect-square rounded-lg overflow-hidden border-2 transition group ${
                  selected
                    ? 'border-[#1f4d3a] shadow-lg'
                    : 'border-[#e3dccb] opacity-60 hover:opacity-100'
                }`}
                data-testid={`willhaben-img-${i}`}
                aria-pressed={selected}
              >
                <img src={u} alt="" className="w-full h-full object-cover" />
                {selected && (
                  <span className="absolute top-1 right-1 p-0.5 rounded-full bg-[#1f4d3a] text-white">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  </span>
                )}
                {i === 0 && selected && (
                  <span className="absolute top-1 left-1 text-[9px] px-1 py-0.5 rounded bg-[#1f4d3a] text-white">
                    Cover
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-[#8a8474] mt-2">
          {t('firstRender.willhabenSelectHint')
            || 'Tipp: Das erste ausgewählte Bild wird oft als Cover verwendet. Wähle ein starkes Außenfoto zuerst.'}
        </p>
      </div>

      <label className="flex items-start gap-3 p-3 rounded-lg bg-[#f7f3ea]/85 border border-[#e3dccb] cursor-pointer hover:border-[#c9c1ac] transition">
        <input
          type="checkbox"
          checked={agree}
          onChange={(e) => setAgree(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-[#1f4d3a] cursor-pointer"
          data-testid="willhaben-agree"
        />
        <span className="text-xs text-[#4a463b] leading-relaxed">
          {t('firstRender.willhabenAgreeText')
            || 'Ich bestätige, dass das mein Inserat ist (oder ich vom Eigentümer beauftragt bin) und dass ich die Bilder im Video verwenden darf.'}
        </span>
      </label>

      <div className="flex gap-2 flex-col sm:flex-row">
        <Button
          type="button"
          onClick={handleImport}
          disabled={!canImport}
          className="flex-1 bg-[#1f4d3a] hover:bg-[#163a2c] text-white disabled:bg-[#e9e2d2] disabled:text-[#8a8474]"
          data-testid="willhaben-import-btn"
        >
          {importing ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <CheckCircle2 className="w-4 h-4 mr-2" />
          )}
          {importing
            ? (t('firstRender.willhabenImporting') || 'Wird importiert…')
            : (t('firstRender.willhabenImportBtn') || 'Importieren und Vorlage wählen')}
        </Button>
        {scraped.source_url && (
          <a
            href={scraped.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm text-[#6f6a5c] hover:text-[#23211a] border border-[#e3dccb] hover:border-[#c9c1ac] transition"
            data-testid="willhaben-view-original"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            {t('firstRender.willhabenOpenOriginal') || 'Original öffnen'}
          </a>
        )}
      </div>
    </Wrapper>
  );
}
